import { useComposedRefs, useDocumentActive, useLatestFn } from '@studio/react-utils/hooks';
import type { ReactElement } from 'react';
import { forwardRef, useEffect, useRef, useState, useMemo } from 'react';
import { twMerge } from 'cn';
import type { Props } from './types.js';
import type { Device } from '@studio/models/device';
import { observeVideoAttachment, traceVideoAttachment } from './attachment-trace.js';
import { VIDEO_STALL_MS } from '../../constants.js';
import { observePresentedFrames } from './presented-frames.js';
import { Offline } from '#~/video/components/viewer/offline.js';
import { BandwidthOverlay } from '#~/video/components/viewer/bandwidth-overlay.js';
import { Track } from 'livekit-client';
import type { Track as StatsTrack } from '#~/livekit/types.js';
import {
  acquireRoomTrackStats,
  areTrackFramesAdvancing,
  createRoomTrackStatsIdentity
} from '#~/livekit/room-track-stats.js';
// ENG-4321 terminal-outcome counting: this stall/retry loop is the real
// >=10-attempt retry-then-succeed flow (up to MAX_RETRY_ATTEMPTS below), so a
// stream that stalls and recovers reads as ONE success, and one that never
// recovers as ONE failure — not one event per 2s retry tick.
import {
  createStreamRecoveryOwner,
  streamRecoveryController,
  type StreamRecoveryAttachment,
  type StreamRecoveryWindow
} from '#~/analytics/domains/stream-recovery.js';

const attachmentMap = new Map<string, NodeJS.Timeout>();

const cancelVideoAttachment = (instanceID: string): void => {
  clearTimeout(attachmentMap.get(instanceID));
  attachmentMap.delete(instanceID);
};

// Test observation is by the exact mounted element, never by a camera or a
// legacy debug-instance key. Expose only a boolean, not the owner/token itself.
const recoveryWindowReaders = new WeakMap<HTMLVideoElement, () => boolean>();
const recoveryObservationEnabled = (): boolean =>
  window.electron?.process?.env?.IRIS_E2E_TEST === '1';

export const readVideoRecoveryWindow = (element: HTMLVideoElement): boolean | undefined => {
  if (!recoveryObservationEnabled()) return;
  return recoveryWindowReaders.get(element)?.();
};

// Maximum number of retry attempts before giving up (prevents infinite memory accumulation)
const MAX_RETRY_ATTEMPTS = 30; // 30 retries * 2 seconds = 60 seconds max
// Maximum time for retry loop in milliseconds (2 minutes)
const MAX_RETRY_DURATION_MS = 2 * 60 * 1000;

// ENG-4231: browsers suppress `timeupdate` (and throttle timers) in a hidden
// tab, so a stall watchdog keyed on `timeupdate` false-positives whenever the
// document is hidden — and the resulting resetDeviceCloudSubscription IS the
// per-camera blackout the watchdog exists to prevent (67 self-inflicted resets
// in one 51-min background session). Stall detection is therefore only
// meaningful while the document is visible. Deliberately gated on
// `document.visibilityState`, NOT `useDocumentActive`: that hook also treats
// plain window focus-loss as inactive, but an unfocused-yet-visible tab still
// paints frames and fires `timeupdate`, so genuine stalls there must still
// recover. Per rule: reference-rules-in-comments (ai.knowledge/global).
const isDocumentHidden = (): boolean =>
  typeof document !== 'undefined' && document.visibilityState === 'hidden';

//@ts-ignore
window.renderRefs = window.renderRefs ?? {};
//@ts-ignore
window.debugRefs = window.debugRefs ?? {};

/**
 * Cleans up debug references for a specific instance.
 * Call this when a video component unmounts to prevent memory leaks.
 */
export const cleanupVideoInstance = (instanceID: string): void => {
  cancelVideoAttachment(instanceID);
  // @ts-ignore
  if (window.renderRefs?.[instanceID]) {
    // @ts-ignore
    delete window.renderRefs[instanceID];
  }
  // @ts-ignore
  if (window.debugRefs?.[instanceID]) {
    // @ts-ignore
    delete window.debugRefs[instanceID];
  }
};

/**
 * Clears all video instance references.
 * Call this during full cleanup or when resetting the application state.
 */
export const cleanupAllVideoInstances = (): void => {
  // Clear all timeouts in attachmentMap
  for (const timeout of attachmentMap.values()) {
    clearTimeout(timeout);
  }
  attachmentMap.clear();

  // Clear all render refs
  // @ts-ignore
  if (window.renderRefs) {
    // @ts-ignore
    window.renderRefs = {};
  }

  // Clear all debug refs
  // @ts-ignore
  if (window.debugRefs) {
    // @ts-ignore
    window.debugRefs = {};
  }
};

/** LiveKit `Video` component. */
export const Video = forwardRef<HTMLVideoElement, Props>(
  (
    {
      className,
      muted = false,
      track,
      traceDeviceId,
      setOnLiveKitChanged,
      governorState,
      roomId,
      onPresentedFrame,
      deviceId,
      handleStall: onHandleStall,
      agentId,
      isEmbedded,
      recoveryOwner,
      ...props
    },
    forwardedRef
  ): ReactElement => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const observationRef = useRef({ track, onPresentedFrame });
    observationRef.current = { track, onPresentedFrame };
    const instanceID = useState(() => Math.random().toString(36).slice(2))[0];
    const stallTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const retryIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const sentinelIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const elementExpiryRef = useRef<NodeJS.Timeout | null>(null);
    const transportExpiryRef = useRef<NodeJS.Timeout | null>(null);
    const elementRepairCountRef = useRef(0);
    const [elementKey, setElementKey] = useState(0);
    const [elementFreshTrack, setElementFreshTrack] = useState<Props['track'] | null>(null);
    const [transportFreshTrack, setTransportFreshTrack] = useState<Props['track'] | null>(null);
    const retryCountRef = useRef<number>(0);
    const retryStartTimeRef = useRef<number | null>(null);
    const owner = useMemo(
      () =>
        recoveryOwner ??
        createStreamRecoveryOwner({
          ...(deviceId !== undefined && deviceId !== '' ? { device_id: deviceId } : {}),
          ...(agentId !== undefined && agentId !== '' ? { agent_id: agentId } : {}),
          ...(typeof isEmbedded === 'boolean' ? { is_embedded: isEmbedded } : {})
        }),
      [recoveryOwner, deviceId, agentId, isEmbedded]
    );
    const recoveryAttachmentRef = useRef<StreamRecoveryAttachment | undefined>(undefined);
    const recoveryWindowRef = useRef<StreamRecoveryWindow | undefined>(undefined);
    const observedElementRef = useRef<HTMLVideoElement | null>(null);
    const readRecoveryWindow = useLatestFn((): boolean => {
      const attachment = recoveryAttachmentRef.current;
      const window = recoveryWindowRef.current;
      return (
        attachment !== undefined &&
        window !== undefined &&
        attachment.pendingWindow() === window &&
        streamRecoveryController.hasOpenWindow(window.key)
      );
    });
    const registerRecoveryObservation = useLatestFn((element: HTMLVideoElement | null): void => {
      const previous = observedElementRef.current;
      if (previous !== null && recoveryWindowReaders.get(previous) === readRecoveryWindow) {
        recoveryWindowReaders.delete(previous);
      }
      observedElementRef.current = element;
      if (element !== null && recoveryObservationEnabled()) {
        recoveryWindowReaders.set(element, readRecoveryWindow);
      }
    });
    useEffect(() => {
      // Composed refs intentionally ignore null. Own explicit cleanup instead
      // of relying on a null callback; re-register on a Strict Mode effect setup.
      const element = observedElementRef.current;
      if (element !== null && recoveryObservationEnabled()) {
        recoveryWindowReaders.set(element, readRecoveryWindow);
      }
      return () => {
        if (element !== null && recoveryWindowReaders.get(element) === readRecoveryWindow) {
          recoveryWindowReaders.delete(element);
        }
      };
    }, [readRecoveryWindow]);

    useEffect(() => {
      // Direct Video consumers own their instance; the outer Viewer disposes
      // its shared owner only when that logical view ends or changes identity.
      return () => {
        if (recoveryOwner === undefined) owner.dispose();
      };
    }, [owner, recoveryOwner]);

    useEffect(() => {
      // A replacement without a media track cannot observe recovery yet. Keep
      // the owner's original no-child deadline running until a track arrives.
      if (track === undefined || track === null) return;
      const attachment = owner.attach();
      recoveryAttachmentRef.current = attachment;
      recoveryWindowRef.current = attachment.pendingWindow();
      return () => {
        if (recoveryAttachmentRef.current === attachment) recoveryAttachmentRef.current = undefined;
        attachment.detach();
      };
    }, [owner, track]);

    const governorPaused = governorState === 'paused-auto' || governorState === 'paused-manual';
    // Intentional suppression is not loss. Viewer also guards cloud resets,
    // but this owner must suspend its reattach/play loop and outcome counting.
    const canObserve = useLatestFn(() => Boolean(track) && !governorPaused && !isDocumentHidden());
    const trace = useLatestFn((event: string, fields: Record<string, unknown> = {}) => {
      traceVideoAttachment(
        event,
        { instanceId: instanceID, deviceId: traceDeviceId },
        videoRef.current,
        track,
        fields
      );
    });

    useEffect(() => {
      const element = videoRef.current;
      if (!element) return;
      return observeVideoAttachment(
        { instanceId: instanceID, deviceId: traceDeviceId },
        element,
        track
      );
    }, [track, traceDeviceId, instanceID, elementKey]);

    // Callback for LiveKit 'changed' event
    const liveKitChangedCallback = useLatestFn(() => {
      attachTrack();
    });

    // @ts-ignore
    window.renderRefs[instanceID] = { videoRef, track };

    // Register callback for LiveKit 'changed' event
    useEffect(() => {
      if (setOnLiveKitChanged) {
        setOnLiveKitChanged(liveKitChangedCallback);
      }
    }, [setOnLiveKitChanged, liveKitChangedCallback]);

    // Function to attach track to video element
    const attachTrack = useLatestFn((initial = false) => {
      const element = videoRef.current;
      if (!element || !track?.mediaStreamID || (!initial && !canObserve())) return;
      trace('attach.scheduled');

      cancelVideoAttachment(instanceID);

      // Attach track with a slight delay to ensure element is ready
      const timeout = setTimeout(() => {
        // Cancellation also fences callbacks already queued by the event loop.
        if (attachmentMap.get(instanceID) !== timeout) return;
        attachmentMap.delete(instanceID);
        if (initial || canObserve()) {
          trace('attach.start');
          try {
            track.attach(element);
            trace('attach.complete');
          } catch (error) {
            trace('attach.failure', { errorName: error instanceof Error ? error.name : 'unknown' });
            throw error;
          }
        }
      }, 0);

      attachmentMap.set(instanceID, timeout);
    });

    // Function to start stall detection
    const startStallDetection = useLatestFn(() => {
      if (stallTimeoutRef.current) clearTimeout(stallTimeoutRef.current);
      // ENG-4231: never ARM the watchdog while hidden — `timeupdate` is
      // suppressed, so it could only ever fire as a false positive. The
      // visibilitychange effect below re-arms a fresh timer on return.
      if (!canObserve()) return;
      stallTimeoutRef.current = setTimeout(() => {
        stallTimeoutRef.current = null;
        // ENG-4231 fire-time guard: a timer armed while visible can still fire
        // after the tab was hidden mid-flight (background timers keep running,
        // throttled). `timeupdate` suppression makes that a false stall — skip
        // the recovery instead of blacking out the stream with a reset.
        if (!canObserve()) return;
        handleStall();
      }, VIDEO_STALL_MS);
    });

    // Decoding frames with no presentation is an element failure: repair the
    // element before considering a disruptive cloud subscription reset.
    const recoverFromStall = useLatestFn(() => {
      const element = videoRef.current;
      if (!element || !track || !canObserve()) return;
      attachTrack();
      void element.play?.()?.catch(() => {});
      if (areTrackFramesAdvancing(track as StatsTrack)) {
        elementRepairCountRef.current += 1;
        if (elementRepairCountRef.current >= 2) {
          elementRepairCountRef.current = 0;
          setElementKey((key) => key + 1);
        }
      } else {
        elementRepairCountRef.current = 0;
        onHandleStall?.();
      }
    });

    // Keep a bounded, low-frequency wakeup after fast recovery is exhausted.
    // It neither resets the cloud subscription nor opens another analytics window.
    const startSentinelLoop = useLatestFn(() => {
      if (sentinelIntervalRef.current) return;
      sentinelIntervalRef.current = setInterval(() => {
        if (!canObserve()) return;
        attachTrack();
        void videoRef.current?.play?.()?.catch(() => {});
      }, 10_000);
    });

    // Function to start continuous retry loop with limits to prevent memory leaks
    const startRetryLoop = useLatestFn(() => {
      if (retryIntervalRef.current || sentinelIntervalRef.current) return;

      trace('recovery.start');

      // Initialize retry tracking
      retryCountRef.current = 0;
      const retryStartedAt = Date.now();
      retryStartTimeRef.current = retryStartedAt;
      // Open the ENG-4321 terminal-outcome window for this stall episode; the
      // controller counts attempts and fires the terminal event at most once.
      // ENG-4321 B1: derive the window from THIS loop's own retry budget so
      // terminalization and the loop's give-up are the same event. Reuse the loop
      // constants (never a second literal) — the window must never be tighter than
      // the retry budget it observes, or a genuine late recovery (ticks 11..30) is
      // booked as a terminal failure. Without the budget the window would fall back
      // to the global 10/60_000 default, which is tighter than 30/120_000.
      const attachment = recoveryAttachmentRef.current;
      const recoveryWindow = attachment?.beginWindow(
        {
          maxAttempts: MAX_RETRY_ATTEMPTS,
          windowMs: MAX_RETRY_DURATION_MS
        },
        retryStartedAt
      );
      recoveryWindowRef.current = recoveryWindow;

      retryIntervalRef.current = setInterval(() => {
        // Recheck at the tick as well as effect cleanup: a hide/pause cannot
        // dispatch a stale recovery action from an already armed interval.
        if (!canObserve()) {
          stopRetryLoop();
          return;
        }
        // Check if we've exceeded retry limits
        retryCountRef.current++;
        const elapsedTime = Date.now() - (retryStartTimeRef.current ?? Date.now());

        // ENG-4321: each retry tick is one failed recovery attempt. The
        // controller counts it and emits stream_recovery_failed only on the
        // TERMINAL attempt (bounded window) — a later recovery within the window
        // supersedes it and emits a success instead.
        attachment?.recordAttempt(recoveryWindow, 'failure');

        if (retryCountRef.current > MAX_RETRY_ATTEMPTS || elapsedTime > MAX_RETRY_DURATION_MS) {
          trace('recovery.exhausted', { attempts: retryCountRef.current, elapsedMs: elapsedTime });
          clearInterval(retryIntervalRef.current!);
          retryIntervalRef.current = null;
          attachment?.cancel();
          startSentinelLoop();
          return;
        }
        recoverFromStall();
      }, 2000);
    });

    // Function to stop retry loop and reset counters
    const stopRetryLoop = useLatestFn(() => {
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current);
        retryIntervalRef.current = null;
      }
      if (sentinelIntervalRef.current) {
        clearInterval(sentinelIntervalRef.current);
        sentinelIntervalRef.current = null;
      }
      elementRepairCountRef.current = 0;
      retryCountRef.current = 0;
      retryStartTimeRef.current = null;
      // Stopping this child's media timers does not end its logical view. Its
      // exact reporting attachment is detached separately on replacement.
    });

    // Handle stall detection
    const handleStall = useLatestFn(() => {
      startRetryLoop();
    });

    // Recovery belongs to the track/observation lifetime, not the video DOM node.
    // Remounting a frozen node must preserve the retry budget and terminal outcome.
    useEffect(() => {
      setElementFreshTrack(null);
      setTransportFreshTrack(null);
      startStallDetection();
      return () => {
        cleanupVideoInstance(instanceID);
        if (stallTimeoutRef.current) clearTimeout(stallTimeoutRef.current);
        if (elementExpiryRef.current) clearTimeout(elementExpiryRef.current);
        if (transportExpiryRef.current) clearTimeout(transportExpiryRef.current);
        stopRetryLoop();
      };
    }, [track, track?.mediaStreamID, governorPaused]);

    // Capture the attached node: after a remount the ref already names its successor.
    useEffect(() => {
      const element = videoRef.current;
      attachTrack(true);
      return () => {
        cancelVideoAttachment(instanceID);
        if (track && element) {
          // Log the captured node rather than its replacement after a remount.
          traceVideoAttachment(
            'detach.start',
            { instanceId: instanceID, deviceId: traceDeviceId },
            element,
            track
          );
          track.detach(element);
          traceVideoAttachment(
            'detach.complete',
            { instanceId: instanceID, deviceId: traceDeviceId },
            element,
            track
          );
        }
      };
    }, [track, track?.mediaStreamID, track?.streamState, elementKey]);

    // Presented frames are stronger evidence than a scheduled media event. On
    // browsers without frame callbacks, require an advancing media clock. Neither
    // RESOLVED inventory nor a timeupdate with unchanged time proves recovery.
    useEffect(() => {
      const element = videoRef.current;
      if (!element || !track || governorPaused) return;
      const attachment = recoveryAttachmentRef.current;
      let retired = false;
      let frameRequest: number | undefined;
      let mediaTime = element.currentTime;
      let presentedFrames: number | undefined;
      const hasFrameCallbacks = typeof element.requestVideoFrameCallback === 'function';

      startStallDetection();
      const progressing = (): void => {
        if (retired || !canObserve()) return;
        setElementFreshTrack(track);
        if (elementExpiryRef.current) clearTimeout(elementExpiryRef.current);
        elementExpiryRef.current = setTimeout(() => setElementFreshTrack(null), 3000);
        // A replacement can recover before starting its own media retry loop.
        // Its exact owner window still records success before retiring playback;
        // ordinary progress without a pending window sends no recovery event.
        if (retryIntervalRef.current) {
          trace('recovery.progress', { attempts: retryCountRef.current });
        }
        // Then note the playback. It must come after this view's own success:
        // playback retires a remount's retained window, which would otherwise
        // drop the very success that proves this view recovered.
        // A reused component showing another camera starts with ordinary
        // playback until that camera opens its own recovery window.
        attachment?.noteProgress(recoveryWindowRef.current);
        stopRetryLoop();
        startStallDetection();
      };
      const handleTimeUpdate = (): void => {
        const next = element.currentTime;
        if (Number.isFinite(next) && next > mediaTime) progressing();
        mediaTime = next;
      };
      const handleFrame: VideoFrameRequestCallback = (_now, metadata) => {
        // A callback already queued at track replacement/unmount must not settle
        // the successor's recovery or schedule another callback on the old track.
        if (retired) return;
        // Live/WebRTC PTS may be zero; frame counts still advance (wicg.github.io/video-rvfc/).
        // Establish a callback baseline; duplicate/regressing counts never prove progress.
        const frames = metadata.presentedFrames;
        const usablePTS = Number.isFinite(metadata.mediaTime) && metadata.mediaTime !== 0;
        if (Number.isSafeInteger(frames) && frames >= 0) {
          if (
            presentedFrames !== undefined &&
            frames > presentedFrames &&
            (!usablePTS || metadata.mediaTime > mediaTime)
          )
            progressing();
          presentedFrames = Math.max(presentedFrames ?? 0, frames);
        }
        mediaTime = metadata.mediaTime;
        frameRequest = element.requestVideoFrameCallback(handleFrame);
      };
      if (hasFrameCallbacks) frameRequest = element.requestVideoFrameCallback(handleFrame);
      else element.addEventListener('timeupdate', handleTimeUpdate);

      return () => {
        retired = true;
        cancelVideoAttachment(instanceID);
        if (frameRequest !== undefined) element.cancelVideoFrameCallback(frameRequest);
        element.removeEventListener('timeupdate', handleTimeUpdate);
      };
    }, [
      track,
      owner,
      deviceId,
      governorPaused,
      elementKey,
      startStallDetection,
      stopRetryLoop,
      canObserve
    ]);

    useEffect(() => {
      if (!onPresentedFrame) {
        return;
      }
      const element = videoRef.current;
      if (!element) {
        return;
      }
      return observePresentedFrames(element, (frame) => {
        // Fence retired tracks and decoder elements before effect cleanup; recovery can replace only the element.
        if (
          observationRef.current.track === track &&
          observationRef.current.onPresentedFrame === onPresentedFrame &&
          videoRef.current === element
        ) {
          onPresentedFrame(frame);
        }
      });
    }, [onPresentedFrame, track, elementKey]);

    // ENG-4231: suspend stall detection across hidden spells. On hide, drop the
    // pending watchdog AND any active retry loop — while hidden, `timeupdate`
    // never arrives, so the loop could not observe a recovery and would keep
    // firing resetDeviceCloudSubscription (the exact self-inflicted blackout
    // this ticket fixes) at every throttled tick. On return to visible, RE-ARM
    // with a fresh 3s window rather than firing immediately: the stream needs
    // one full window to prove itself before recovery may kick in, and a
    // genuine stall is re-detected 3s later anyway.
    useEffect(() => {
      const handleVisibilityChange = (): void => {
        if (document.visibilityState === 'hidden') {
          cancelVideoAttachment(instanceID);
          if (stallTimeoutRef.current) {
            clearTimeout(stallTimeoutRef.current);
            stallTimeoutRef.current = null;
          }
          stopRetryLoop();
          recoveryAttachmentRef.current?.cancel();
        } else {
          startStallDetection();
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [startStallDetection, stopRetryLoop]);

    // Per knowledge use-document-active-seam (platform): `useDocumentActive` is
    // the app's canonical fg/bg signal — it fires on window focus/blur (the
    // plain focus-loss that `visibilitychange` misses) with a 250ms debounce.
    // On a background->foreground return Chromium may have paused/blanked the
    // tray <video> and nothing re-binds it, so the tile stays black until a
    // reload. On the inactive->active edge, re-run attachTrack() AND replay the
    // element to recover the preview.
    const documentActive = useDocumentActive();
    const wasDocumentActiveRef = useRef(documentActive);
    useEffect(() => {
      const wasActive = wasDocumentActiveRef.current;
      wasDocumentActiveRef.current = documentActive;
      // Only act on the background(false)->foreground(true) transition, so a
      // steady-state re-render never re-attaches.
      if (documentActive && !wasActive && !governorPaused) {
        trace('foreground.attach');
        attachTrack();
        // play() may reject under autoplay policy (the element is muted);
        // swallow the rejection so it never surfaces as an unhandled rejection.
        void videoRef.current?.play?.()?.catch((error: unknown) => {
          trace('play.rejected', {
            reason: 'foreground',
            errorName: error instanceof Error ? error.name : 'unknown'
          });
        });
      }
    }, [documentActive, attachTrack, governorPaused]);

    // A thumbnail may leave and re-enter the viewport without a window focus event.
    useEffect(() => {
      const element = videoRef.current;
      if (!element || typeof IntersectionObserver === 'undefined') return;
      let retired = false;
      let wasIntersecting: boolean | undefined;
      const observer = new IntersectionObserver(
        (entries) => {
          if (retired) return;
          const entry = entries[0];
          if (!entry) return;
          if (entry.isIntersecting && wasIntersecting === false && canObserve()) {
            attachTrack();
            void element.play?.()?.catch(() => {});
          }
          wasIntersecting = entry.isIntersecting;
        },
        { threshold: 0.1 }
      );
      observer.observe(element);
      return () => {
        retired = true;
        observer.disconnect();
      };
    }, [track, elementKey, attachTrack, canObserve]);

    // Share the room's stats poller; track replacement retires the lease and
    // stale samples expire even when getStats stops resolving altogether.
    useEffect(() => {
      if (!track || !roomId || !deviceId || governorPaused) return;
      const statsTrack = track as StatsTrack;
      const lease = acquireRoomTrackStats({
        // Props carry a plain string id (main #1867); the stats registry keys on the branded id.
        identity: createRoomTrackStatsIdentity(
          roomId,
          deviceId as Device['id'],
          'video',
          statsTrack
        ),
        track: statsTrack,
        refreshInterval: 1000,
        listener: () => {
          if (!canObserve() || !areTrackFramesAdvancing(statsTrack)) return;
          setTransportFreshTrack(track);
          if (transportExpiryRef.current) clearTimeout(transportExpiryRef.current);
          transportExpiryRef.current = setTimeout(() => setTransportFreshTrack(null), 3000);
        }
      });
      return () => {
        lease.release();
        if (transportExpiryRef.current) clearTimeout(transportExpiryRef.current);
        setTransportFreshTrack(null);
      };
    }, [track, roomId, deviceId, governorPaused, canObserve]);

    const ref = useComposedRefs(videoRef, forwardedRef, registerRecoveryObservation);

    const available = Boolean(track);
    const active = track?.streamState === Track.StreamState.Active;
    const recentlyLive =
      Boolean(track) && (elementFreshTrack === track || transportFreshTrack === track);

    return (
      <div className={twMerge('relative aspect-video overflow-hidden bg-black', className)}>
        <video
          key={elementKey}
          className={active || recentlyLive ? 'h-full w-full rounded-inherit' : 'h-0 w-0'}
          muted={muted}
          ref={ref}
          {...props}
        />
        {active && governorState === 'quality-capped' && (
          <BandwidthOverlay governorState={governorState} />
        )}
        {/* A stale governor event must never mask a publication that no longer exists. */}
        {!available && <Offline {...props} />}
        {/* Pause overlays key off the COMMITTED governor state, not the track's
            streamState. streamState only flips via the server's StreamStateUpdate
            signal message — an async echo that lags the pause on healthy networks
            and is lost entirely on a degraded signal channel (ENG-3548 HIL run
            29454695954: media froze after setEnabled(false) but streamState stayed
            'active', so the old `!active` gate left a frozen frame with no paused
            indicator). The client already knows its own committed pause; the user
            feedback must not depend on a lossy server round-trip.
            Per rule: reference-rules-in-comments (ai.knowledge/global). */}
        {available && governorPaused && <BandwidthOverlay governorState={governorState} />}
        {available && !active && !governorPaused && !recentlyLive && <Offline {...props} />}
      </div>
    );
  }
);
